/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';
import {
    ISearchCategoryViewProps,
    ISearchFormViewProps,
    ISearchKeywordViewProps,
    ISearchProductViewProps
} from '@msdyn365-commerce-modules/search/src/modules/search/./components';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import Msdyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ISearchViewProps } from './mfrm-search';
const SearchView: React.FC<ISearchViewProps> = props => {
    const {
        Search,
        AutoSuggestAriaLabel,
        AutoSuggestAriaLabelText,
        searchText,
        AutoSuggest,
        KeywordSuggest,
        ProductSuggest,
        CategorySuggest,
        UlKeyword,
        UlProduct,
        UlCategory,
        form,
        autosuggestCategory,
        autosuggestKeyword,
        autosuggestProduct,
        SearchForm,
        FormWrapper,
        label,
        context: { app },
        resources: { seeAllResultButtonText, categoryTitleText }
    } = props;
     const searchInputMinLength = app.config.searchInputMinLength || 3;
    return (
                <Module {...Search}>

                    <Node
                        className={`ms-search-overlay ${
                            (autosuggestCategory || autosuggestProduct) && searchText && searchText.length >= searchInputMinLength ? 'show' : 'hide'
                        }`}
                    />
                    <Node className='ms-search__holder'>
                        {label}
                        {_renderForm(form as ISearchFormViewProps, SearchForm, FormWrapper)}

                        {searchText && searchText.length >= searchInputMinLength ? (
                            <Node {...AutoSuggest}>
                                <Node className={'ms-search__autoSuggest__scroll'}>
                                    {searchText && searchText.length > 0 ? <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''}
                                    {_renderKeywordSuggestions(KeywordSuggest, UlKeyword, autosuggestKeyword)}
                                    {_renderProductSuggestions(ProductSuggest, UlProduct, autosuggestProduct)}
                                    {_renderSearchAllButton(props, seeAllResultButtonText, autosuggestProduct, autosuggestCategory)}
                                    {_renderCategorySuggestions(CategorySuggest, UlCategory, categoryTitleText, autosuggestCategory)}
                                </Node>
                            </Node>
                        ) : (
                            ''
                        )}
                    </Node>
                </Module>         );
};

const _renderForm = (form: ISearchFormViewProps, SearchForm: INodeProps, FormWrapper: INodeProps) => {
    return (
        <Node {...SearchForm}>
            <Node {...FormWrapper}>
                {form.submitBtn}
                {form.input}
                {form.cancelBtn}
            </Node>
        </Node>
    );
};

const _renderKeywordSuggestions = (KeywordSuggest: INodeProps, UlKeyword: INodeProps, keywordSuggestions?: ISearchKeywordViewProps) => {
    return (
        keywordSuggestions && (
            <Node {...KeywordSuggest}>
                <Node {...UlKeyword}>
                    {keywordSuggestions.text.map(text => {
                        return text;
                    })}
                </Node>
            </Node>
        )
    );
};

const _renderProductSuggestions = (ProductSuggest: INodeProps, UlProduct: INodeProps, productSuggestions?: ISearchProductViewProps) => {
    return (
        productSuggestions && (
            <Node {...ProductSuggest}>
                <Node {...UlProduct}>
                    {productSuggestions.title}
                    {productSuggestions.items.map((item, index) => {
                        return (
                            <Node {...item.LiProduct} key={item.id || index}>
                                <Node {...item.AProduct}>
                                    {/* VSI Customization - Start */}
                                    {/* {item.thumbnail} */}
                                    {item.text}
                                    {/* {item.price} */}
                                    {/* VSI Customization - End */}
                                </Node>
                            </Node>
                        );
                    })}
                </Node>
            </Node>
        )
    );
};

const _renderCategorySuggestions = (
    CategorySuggest: INodeProps,
    UlCategory: INodeProps,
    categoryTitleText: string,
    categorySuggestions?: ISearchCategoryViewProps
) => {
    return (
        categorySuggestions && (
            <Node {...CategorySuggest}>
                <Node {...UlCategory}>
                    {/* VSI Customization - Start */}
                    {/* {categorySuggestions.title} */}
                    <Node className='msc-autoSuggest__categoryResults-title' tag='li'>
                        {categoryTitleText}
                    </Node>
                    {/* VSI Customization - END */}
                    {categorySuggestions.text.map(text => {
                        return text;
                    })}
                </Node>
            </Node>
        )
    );
};
// VSI Customization - Start
const handleSearchAllResult = async (props: React.PropsWithChildren<ISearchViewProps>): Promise<void> => {

    const {
        searchText,
        config: { disableSubmitSearch },
        context: {
            actionContext,
            app: {
                config: { searchQueryStringParameter }
            }
        }
    } = props;

    const query = searchText && searchText;

    if (!query || (query && query.length === 0) || disableSubmitSearch) {
        // this.searchTextInput && searchText && searchText.focus();
    } else {
        const searchQsp = searchQueryStringParameter || 'q';
        const searchURL = _appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
        // @ts-ignore
        if ('utag' in window) {
            // @ts-ignore
            utag.link({
                event: 'site_search',
                event_type: 'site_search',
                search_term: props.searchText,
                search_type: 'see-all-results-button'
            });
        }

        const redirectUrl = await props.handleUnbxdRedirect(query);

        if (Msdyn365.isBrowser) {
            document.location.href = redirectUrl ? redirectUrl : searchURL;
        }

    }
};

const _renderSearchAllButton = (
    props: React.PropsWithChildren<ISearchViewProps>,
    seeAllResultButtonText: string,
    productSuggestions?: ISearchProductViewProps,
    categorySuggestions?: ISearchCategoryViewProps
) => {
    const moduleName = 'ms-search__autoSuggest';
    return (
        (categorySuggestions || productSuggestions) && (
            <Node
                tag='a'
                className={`${moduleName}__see-all-results`}
                role='button'
                href='javascript:void(0)'
                // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                onClick={() => handleSearchAllResult(props)}
                tabIndex='0'
            >
                {seeAllResultButtonText}
            </Node>
        )
    );
};

const _appendQueryParams = (route: string, query: string, qsp: string): string => {
    if (route && route.length > 0) {
        return query && query.length > 0 ? `${route}${route.indexOf('?') === -1 ? '?' : '&'}${qsp}=${encodeURIComponent(query)}` : route;
    }
    return '';
};
// VSI Customization - END
export default SearchView;
